import { Avatar, Box, Button, Card, CardActions, CardContent, CardHeader, CircularProgress, Fab, Grid, makeStyles, SvgIcon, Theme, Typography } from "@material-ui/core";
import { cyan, green, red, yellow } from "@material-ui/core/colors";
import AddShoppingCartIcon from '@material-ui/icons/AddShoppingCart';
import BusinessIcon from '@material-ui/icons/Business';
import DoneIcon from '@material-ui/icons/Done';
import KeyboardCapslockIcon from '@material-ui/icons/KeyboardCapslock';
import LastPageIcon from '@material-ui/icons/LastPage';
import SaveOutlinedIcon from '@material-ui/icons/SaveOutlined';
import { Result } from '@zxing/library';
import React, { FunctionComponent, useEffect, useState } from "react";
import { useHistory, useRouteMatch } from "react-router-dom";
import * as Tone from "tone";
import { GetReceiptItem, ListReceiptItem, PickedPackage, useGetPickingReceiptQuery, usePackagePickedMutation, useReceiptPickedMutation } from "../../graphql/generated/graphql";
import BarcodeDialog from '../../shared/BarcodeDialog';
import BarcodeKeyboard from "../../shared/BarcodeKeyboard";
import useArray from '../../shared/Hooks/useArray';
import Beep from "./Beep";
import Counter from "./Counter";
import ReceiptItem from "./ReceiptItem";

const useStyles = makeStyles((theme: Theme) => ({
  layoutRoot: {
    marginTop: theme.spacing(2),
  },
  root: {
    paddingLeft: theme.spacing(1),
  },
  card: {
    marginRight: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  cardHeader: {
    "& .MuiCardHeader-title": {
      fontWeight: "bold",
    },
  },
  warehouseAvatar: {
    color: theme.palette.action.active,
  },
  warehouseAvatarExecutable: {
    backgroundColor: yellow[600],
    color: theme.palette.getContrastText(yellow[600]),
  },
  warehouseAvatarExecuted: {
    backgroundColor: green[600],
    color: theme.palette.getContrastText(green[600]),
  },
  item: {
    marginRight: theme.spacing(1),
  },
  filters: {
    marginLeft: "auto",
    marginRight: "auto",
    width: 48 * 2,
    marginBottom: theme.spacing(2),
  },
  progress: {
    marginLeft: "auto",
    marginRight: "auto",
    width: 40,
  },
  remaining: {
    color: theme.palette.getContrastText(red[600]),
    backgroundColor: red[600],
  },
  completed: {
    color: theme.palette.getContrastText(green[600]),
    backgroundColor: green[600],
  },
  startScan: {
    backgroundColor: cyan.A400,
    position: 'fixed',
    bottom: theme.spacing(2),
    right: theme.spacing(2),
  },
  complete: {
    backgroundColor: green.A400,
    position: 'fixed',
    bottom: theme.spacing(2),
    right: theme.spacing(2),
  },
  lastReceiptItem: {
    backgroundColor: theme.palette.background.paper,
    color: green[600],
  },
  rightAlignItem: {
    marginLeft: "auto",
  },
}));

let prevBarcode = "";

export default function Component()
{
  const classes = useStyles();
  const match = useRouteMatch<{ receiptId: string }>("/picking/:receiptId");
  const { data, loading, error } = useGetPickingReceiptQuery({
    variables: {
      ReceiptId: Number.parseInt(match?.params.receiptId ?? ""),
    },
  });

  const [getReceiptResult, setGetReceiptResult] = useState(data?.GetReceipt);

  useEffect(() =>
  {
    if(data && data.GetReceipt)
    {
      let newGetReceiptResult = {
        Receipt: { ...data.GetReceipt.Receipt! },
        Items: [...data.GetReceipt.Items!]
      };
      setGetReceiptResult(newGetReceiptResult);
      const completed: { [key: number]: number } = [];
      data.GetReceipt.Items
        .filter(i => i.PickedAt)
        .forEach(i => { completed[i.ReceiptPackageId] = i.Quantity });
      setCompleted(completed);
    }
  }, [data]);

  const [completed, setCompleted] = useState<{ [key: number]: number }>({});
  const [barcodeDialogOpen, setBarcodeDialogOpen] = useState(false);
  const [lastReceiptPackageId, setLastReceiptPackageId] = useState<number | null>(null);
  const { contains: isProgress, add: setProgress, remove: clearProgress } = useArray();

  function getCompleted(): number
  {
    let c = Object.values(completed).reduce((a, b) => a + b, 0);
    return c ?? 0;
  }

  function getRemaining(): number
  {
    let quantities = getReceiptResult?.Items.filter(i => i.Execute).map(i => i.Quantity).reduce((a, b) => a + b, 0);
    return (quantities ?? 0) - getCompleted();
  }

  function getItemCompleted(receiptPackageId: number): number
  {
    let c = completed[receiptPackageId];
    return c ?? 0;
  }

  function getItemRemaining(receiptPackageId: number): number
  {
    let item = getReceiptResult?.Items.find(i => i.ReceiptPackageId === receiptPackageId);
    return (item?.Quantity ?? 0) - getItemCompleted(receiptPackageId);
  }


  const [receiptPicked, receiptPickedMutation] = useReceiptPickedMutation();
  const history = useHistory();

  const pickedButtonClicked = async () =>
  {
    const receiptPickedResult = await receiptPicked({ variables: { ReceiptId: Number.parseInt(match?.params.receiptId ?? "") } });
    if(receiptPickedResult.data?.ReceiptPicked)
    {
      receiptPickedMutation.client.clearStore();
      history.goBack();
    }
  };

  const Receipt: FunctionComponent<{ Item: ListReceiptItem }> = (props) =>
  {

    function getIcon(receiptTypeId?: number)
    {
      switch(receiptTypeId)
      {
        case 1: return <LastPageIcon />;
        case 2: return <KeyboardCapslockIcon />;
        default: return <BusinessIcon />;
      }
    }

    function getAvatarClass(receiptStateId?: number)
    {
      if(getRemaining() === 0) return classes.warehouseAvatarExecuted;
      switch(receiptStateId)
      {
        case 1: return classes.warehouseAvatar;
        case 2: return classes.warehouseAvatarExecutable;
        case 3: return classes.warehouseAvatarExecuted;
        default: return classes.warehouseAvatar
      }
    }

    return <Grid item xs={12} sm={6} md={4} lg={3}>
      <Card className={classes.card}>
        <CardHeader title={`#${props.Item.ReceiptId}`}
          subheader={`${props.Item.ReceiptType?.Name}/Előkészítés: ${props.Item.ReceiptState?.Name}${props.Item.Ramp?.Name ? ", Rámpa: " + props.Item.Ramp?.Name : ""}`}
          avatar={<Avatar className={getAvatarClass(props.Item.ReceiptState?.ReceiptStateId ?? undefined)} >{getIcon(props.Item.ReceiptType?.ReceiptTypeId ?? undefined)}</Avatar>} />
        <CardContent>
          <Grid container>
            {props.Item.Date ? <Grid item className={classes.item}><Typography>{props.Item.Date}: </Typography></Grid> : <></>}
            {props.Item.Partner?.Name ? <Grid item className={classes.item}><Typography>{props.Item.Partner?.Name} </Typography></Grid> : <></>}
            <Grid item><Typography>{props.Item.TruckPlateNumber} {props.Item.TrailerPlateNumber}</Typography></Grid>
          </Grid>
        </CardContent>
        <CardActions disableSpacing>
          <Counter className={classes.remaining} count={getRemaining()} limit={0} />
          <Counter className={classes.completed} count={getCompleted()} limit={1} />
          <Button
            variant="contained"
            disabled={getRemaining() > 0}
            className={classes.rightAlignItem}
            startIcon={<SaveOutlinedIcon />}
            onClick={pickedButtonClicked}
          >Előkészítve</Button>
        </CardActions>
      </Card>
    </Grid>;
  }

  function onBarcodeUpdate(err: unknown, result: Result | undefined)
  {
    if(result)
    {
      const barcode = result.getText();
      if(barcode !== prevBarcode)
      {
        prevBarcode = barcode;
        onBarcode(barcode);
      }
    }
  }

  async function onBarcode(barcode: string)
  {
    let item = getReceiptResult?.Items.find(i => i.PackageNumber === barcode || i.SecondaryPackageNumber === barcode);
    if(item)
    {
      let itemCompleted = getItemCompleted(item.ReceiptPackageId);
      if(itemCompleted <= item.Quantity)
      {
        await Beep(880, "square");
        setBarcodeDialogOpen(false);
        completed[item.ReceiptPackageId] = item?.Quantity!;
        setCompleted(completed);
        itemPicked(item!);
        setLastReceiptPackageId(item!.ReceiptPackageId);
        return;
      }
    }
    Beep(440, "sine");
  }

  useEffect(() =>
  {
    if(lastReceiptPackageId)
    {
      refs[lastReceiptPackageId].current!.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
    // eslint-disable-next-line
  }, [lastReceiptPackageId])

  const [packagePicked] = usePackagePickedMutation();

  const itemPicked = async (item: GetReceiptItem) =>
  {
    setProgress(item.ReceiptPackageId);
    const pickedPackage: PickedPackage = {
      ReceiptPackageId: item.ReceiptPackageId,
    };
    const result = await packagePicked({ variables: { PickedPackage: pickedPackage } });
    item = { ...item };
    item.PickedAt = result.data?.PackagePicked.PickedAt;
    const existingItemIndex = getReceiptResult!.Items.findIndex(i => i.ReceiptPackageId === item.ReceiptPackageId);
    clearProgress(item.ReceiptPackageId);
    getReceiptResult!.Items[existingItemIndex!] = item;
    setGetReceiptResult({ ...getReceiptResult! });
  }

  const refs = getReceiptResult?.Items.reduce<{ [key: number]: React.RefObject<HTMLElement> }>((acc, value) =>
  {
    acc[value.ReceiptPackageId] = React.createRef();
    return acc;
  }, [])!;

  const States: FunctionComponent = () =>
  {
    if(loading || !getReceiptResult) return <Box className={classes.progress}><CircularProgress color="secondary" /></Box>;
    if(error) return <Typography>{error.message}</Typography>;
    return <>
      <Grid container className={classes.root}>
        <Receipt Item={getReceiptResult?.Receipt!} />
        {getReceiptResult?.Items.map(i => <ReceiptItem
          key={i.ReceiptPackageId}
          Item={i}
          Icon={<AddShoppingCartIcon />}
          Subheader={i.PickedAt ? 'Előkészítve' : 'Előkészítés'}
          IsProgress={isProgress(i.ReceiptPackageId)}
          Remaining={getItemRemaining(i.ReceiptPackageId)}
          Bookmark={i.ReceiptPackageId === lastReceiptPackageId}
          Ref={refs[i.ReceiptPackageId]}
        />)}
      </Grid>
      <BarcodeKeyboard enabled={!barcodeDialogOpen} onBarcodeUpdate={onBarcode} />
      <BarcodeDialog dialogOpen={barcodeDialogOpen} onBarcodeUpdate={onBarcodeUpdate} onCancel={() => { setBarcodeDialogOpen(false) }} />
      {getRemaining() === 0 ?
        <Fab className={classes.complete} onClick={() =>
        {
        }} >
          <DoneIcon />
        </Fab>
        :
        <Fab className={classes.startScan} onClick={() =>
        {
          Tone.context.resume().then(() =>
          {
            prevBarcode = "";
            setBarcodeDialogOpen(true);
            Tone.context.resume();
          });
        }} >
          <SvgIcon>
            <path stroke="null" id="svg_3" d="m1.046054,4.697369c0,-0.576125 0.467108,-1.043233 1.043233,-1.043233l2.608082,0l0,-1.043233l-2.608082,0c-1.152251,0 -2.086466,0.934215 -2.086466,2.086466l0,2.608082l1.043233,0l0,-2.608082zm0,14.605261l0,-2.608082l-1.043233,0l0,2.608082c0,1.152251 0.934215,2.086466 2.086466,2.086466l2.608082,0l0,-1.043233l-2.608082,0c-0.576125,0 -1.043233,-0.467108 -1.043233,-1.043233zm21.907892,0c0,0.576125 -0.467108,1.043233 -1.043233,1.043233l-2.608082,0l0,1.043233l2.608082,0c1.152251,0 2.086466,-0.934215 2.086466,-2.086466l0,-2.608082l-1.043233,0l0,2.608082zm-1.043233,-16.691727l-2.608082,0l0,1.043233l2.608082,0c0.576125,0 1.043233,0.467108 1.043233,1.043233l0,2.608082l1.043233,0l0,-2.608082c0,-1.152251 -0.934215,-2.086466 -2.086466,-2.086466zm-20.864659,9.389096c0,0.287932 0.233684,0.521616 0.521616,0.521616l20.864659,0c0.287932,0 0.521616,-0.233684 0.521616,-0.521616s-0.233684,-0.521616 -0.521616,-0.521616l-20.864659,0c-0.287932,0 -0.521616,0.233684 -0.521616,0.521616zm8.345864,-6.259398l-1.043233,0l0,4.694548l1.043233,0l0,-4.694548zm-6.259398,0l0,4.694548l1.043233,0l0,-4.694548l-1.043233,0zm8.345864,0l-1.043233,0l0,4.694548l1.043233,0l0,-4.694548zm5.216165,0l-1.043233,0l0,4.694548l1.043233,0l0,-4.694548zm2.086466,0l-1.043233,0l0,4.694548l1.043233,0l0,-4.694548zm1.043233,0l0,4.694548l1.043233,0l0,-4.694548l-1.043233,0zm-12.518795,0l-2.086466,0l0,4.694548l2.086466,0l0,-4.694548zm7.302631,0l-2.086466,0l0,4.694548l2.086466,0l0,-4.694548zm-6.259398,12.518795l1.043233,0l0,-4.694548l-1.043233,0l0,4.694548zm-4.172932,0l0,-4.694548l-1.043233,0l0,4.694548l1.043233,0zm6.259398,0l1.043233,0l0,-4.694548l-1.043233,0l0,4.694548zm5.216165,0l1.043233,0l0,-4.694548l-1.043233,0l0,4.694548zm2.086466,0l1.043233,0l0,-4.694548l-1.043233,0l0,4.694548zm3.129699,0l0,-4.694548l-1.043233,0l0,4.694548l1.043233,0zm-15.648494,0l2.086466,0l0,-4.694548l-2.086466,0l0,4.694548zm7.302631,0l2.086466,0l0,-4.694548l-2.086466,0l0,4.694548z" clip-rule="evenodd" fill-rule="evenodd" />
          </SvgIcon>
        </Fab>
      }
    </>;
  }

  return (
    <Box className={classes.layoutRoot}>
      <States />
    </Box>
  );
}
